import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import Button from "../../../shared/components/Button";
import OrganizerTimeInput from "../../../shared/components/OrganizerTimeInput";
import {
  getTournamentSchedule,
  type AdminTournamentScheduleConfig,
} from "../services/eventRegistrationAdminService";
import {
  getAdminTournamentEventSchedule,
  saveAdminTournamentEventSchedule,
  type TournamentEventSchedule,
  type TournamentEventScheduleActivity,
  type TournamentEventScheduleDay,
} from "../../challenges/services/challengeAdminService";
import {
  addLocalDays,
  calendarDaysInclusive,
  dateOnlyFromLocal,
  timeOnlyFromLocal,
  toDateTimeLocal,
  weekdayKeyFromDate,
} from "../utils/tournamentOperationSchedule";
import "./AdminTournamentEventSchedulePanel.css";

type AdminTournamentEventSchedulePanelProps = {
  token: string;
  tournamentId: number;
  scheduleStartAt?: string | null;
  scheduleEndAt?: string | null;
  onError: (message: string) => void;
  onPendingChangesChange?: (items: string[]) => void;
  onSuccess: (message: string) => void;
};

export type AdminTournamentEventSchedulePanelHandle = {
  savePendingChanges: () => Promise<boolean>;
};

function makeLocalId(prefix: string) {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

type ScheduleDayWindow = {
  date: string;
  startTime: string;
  endTime: string;
};

function makeDefaultDay(
  window: ScheduleDayWindow,
  index: number,
): TournamentEventScheduleDay {
  return {
    id: `configured-day-${window.date}`,
    date: window.date,
    title: "",
    startTime: window.startTime,
    endTime: window.endTime,
    displayOrder: index,
    isActive: true,
    activities: [],
  };
}

function makeDefaultActivity(index: number): TournamentEventScheduleActivity {
  return {
    id: makeLocalId("activity"),
    title: "",
    startTime: "08:00",
    endTime: "",
    type: "general",
    customTypeName: "",
    categoryKey: "",
    categoryLabel: "",
    locationLabel: "",
    description: "",
    isHighlighted: false,
    displayOrder: index,
    isActive: true,
  };
}

function formatDateLabel(date: string) {
  const [year, month, day] = String(date || "")
    .split("-")
    .map((item) => Number(item));
  if (!year || !month || !day) return date || "Data não configurada";
  return new Intl.DateTimeFormat("pt-BR", {
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
  }).format(new Date(year, month - 1, day));
}

function sortDays(days: TournamentEventScheduleDay[]) {
  return [...days]
    .sort(
      (a, b) =>
        a.date.localeCompare(b.date) ||
        a.displayOrder - b.displayOrder ||
        a.id.localeCompare(b.id),
    )
    .map((day, index) => ({ ...day, displayOrder: index }));
}

function sortActivities(activities: TournamentEventScheduleActivity[]) {
  return [...activities]
    .sort(
      (a, b) =>
        a.startTime.localeCompare(b.startTime) ||
        a.displayOrder - b.displayOrder ||
        a.id.localeCompare(b.id),
    )
    .map((activity, index) => ({ ...activity, displayOrder: index }));
}

function buildConfiguredDayWindows({
  config,
  scheduleEndAt,
  scheduleStartAt,
}: {
  config?: AdminTournamentScheduleConfig | null;
  scheduleEndAt?: string | null;
  scheduleStartAt?: string | null;
}) {
  const sourceStart = config?.dayStartAt || scheduleStartAt || "";
  const sourceEnd = config?.dayEndAt || scheduleEndAt || sourceStart;
  const startLocal = toDateTimeLocal(sourceStart);
  const endLocal = toDateTimeLocal(sourceEnd || sourceStart);
  const firstDay = dateOnlyFromLocal(startLocal);
  if (!firstDay) return [];
  const daysCount = calendarDaysInclusive(startLocal, endLocal || startLocal);
  const hasScheduleConfig = Boolean(config?.dayStartAt || config?.dayEndAt);
  const fallbackStartTime =
    config?.operatingHours?.[0]?.startTime ||
    (hasScheduleConfig ? timeOnlyFromLocal(startLocal, "08:00") : "08:00");
  const fallbackEndTime =
    config?.operatingHours?.[0]?.endTime ||
    (hasScheduleConfig ? timeOnlyFromLocal(endLocal || startLocal, "18:00") : "18:00");
  return Array.from({ length: daysCount }, (_item, index) => {
    const date = addLocalDays(firstDay, index);
    const dayRule = (config?.operatingHours || []).find(
      (rule) => rule.dayOfWeek === weekdayKeyFromDate(date),
    );
    return {
      date,
      startTime: dayRule?.startTime || fallbackStartTime,
      endTime: dayRule?.endTime || fallbackEndTime,
    };
  });
}

function mergeScheduleWithConfiguredDays(
  data: TournamentEventSchedule,
  windows: ScheduleDayWindow[],
) {
  const savedByDate = new Map(
    (data.days || []).map((day) => [day.date, day] as const),
  );
  const days = windows.map((window, index) => {
    const saved = savedByDate.get(window.date);
    const base = saved || makeDefaultDay(window, index);
    return {
      ...base,
      date: window.date,
      startTime: window.startTime,
      endTime: window.endTime,
      displayOrder: index,
      activities: sortActivities(base.activities || []),
    };
  });
  return { ...data, days };
}

function isMissingEventScheduleError(error: unknown) {
  const message = error instanceof Error ? error.message : String(error || "");
  const normalized = message.trim().toLowerCase();
  return (
    normalized === "recurso não encontrado." ||
    normalized === "recurso nao encontrado." ||
    normalized === "resource not found." ||
    normalized.includes("cannot get /api/admin/tournaments/")
  );
}

function formatScheduleSaveError(error: unknown) {
  if (isMissingEventScheduleError(error)) {
    return "A API do cronograma não está disponível. Reinicie o backend e tente salvar novamente.";
  }
  return error instanceof Error ? error.message : "Erro ao salvar cronograma.";
}

function buildLocalWarnings(schedule: TournamentEventSchedule) {
  const warnings: string[] = [];
  schedule.days.forEach((day, dayIndex) => {
    const dayLabel = day.title || day.date || `Dia ${dayIndex + 1}`;
    day.activities.forEach((activity, activityIndex) => {
      const activityLabel = activity.title || `Atividade ${activityIndex + 1}`;
      if (!activity.title.trim()) warnings.push(`${dayLabel}: atividade sem titulo.`);
      if (day.startTime && activity.startTime && activity.startTime < day.startTime) {
        warnings.push(`${activityLabel}: inicio fora do horario do dia.`);
      }
      if (day.endTime && activity.endTime && activity.endTime > day.endTime) {
        warnings.push(`${activityLabel}: fim fora do horario do dia.`);
      }
      const next = day.activities[activityIndex + 1];
      if (next && activity.endTime && activity.endTime > next.startTime) {
        warnings.push(`${activityLabel}: conflito de horario.`);
      }
    });
  });
  return warnings;
}

function scheduleSnapshot(schedule: TournamentEventSchedule) {
  return JSON.stringify({
    isPublicEnabled: Boolean(schedule.isPublicEnabled),
    days: sortDays(schedule.days || []).map((day) => ({
      id: day.id,
      date: day.date,
      title: String(day.title || "").trim(),
      startTime: day.startTime || "",
      endTime: day.endTime || "",
      displayOrder: day.displayOrder || 0,
      isActive: Boolean(day.isActive),
      activities: sortActivities(day.activities || []).map((activity) => ({
        id: activity.id,
        title: String(activity.title || "").trim(),
        startTime: activity.startTime || "",
        endTime: activity.endTime || "",
        description: String(activity.description || "").trim(),
        displayOrder: activity.displayOrder || 0,
        isActive: Boolean(activity.isActive),
      })),
    })),
  });
}

const AdminTournamentEventSchedulePanel = forwardRef<
  AdminTournamentEventSchedulePanelHandle,
  AdminTournamentEventSchedulePanelProps
>(function AdminTournamentEventSchedulePanel({
  token,
  tournamentId,
  scheduleStartAt,
  scheduleEndAt,
  onError,
  onPendingChangesChange,
  onSuccess,
}, ref) {
  const [schedule, setSchedule] = useState<TournamentEventSchedule>({
    isPublicEnabled: false,
    days: [],
  });
  const [baselineSnapshot, setBaselineSnapshot] = useState("");
  const [selectedDayId, setSelectedDayId] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [scheduleConfig, setScheduleConfig] =
    useState<AdminTournamentScheduleConfig | null>(null);
  const selectedDay = schedule.days.find((day) => day.id === selectedDayId);
  const warnings = useMemo(() => buildLocalWarnings(schedule), [schedule]);
  const currentSnapshot = useMemo(() => scheduleSnapshot(schedule), [schedule]);
  const hasPendingChanges =
    Boolean(baselineSnapshot) && baselineSnapshot !== currentSnapshot;

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([
      getAdminTournamentEventSchedule(token, tournamentId).catch((error) => {
        if (!isMissingEventScheduleError(error)) throw error;
        return { isPublicEnabled: false, days: [] } satisfies TournamentEventSchedule;
      }),
      getTournamentSchedule(token, tournamentId).catch(() => null),
    ])
      .then(([data, tournamentSchedule]) => {
        if (!active) return;
        const windows = buildConfiguredDayWindows({
          config: tournamentSchedule?.config || null,
          scheduleEndAt,
          scheduleStartAt,
        });
        setScheduleConfig(tournamentSchedule?.config || null);
        const nextSchedule = mergeScheduleWithConfiguredDays(data, windows);
        setSchedule(nextSchedule);
        setBaselineSnapshot(scheduleSnapshot(nextSchedule));
        setSelectedDayId(nextSchedule.days[0]?.id || "");
      })
      .catch((error) => {
        if (active) onError(error instanceof Error ? error.message : "Erro ao carregar cronograma.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [onError, scheduleEndAt, scheduleStartAt, token, tournamentId]);

  function updateDay(dayId: string, patch: Partial<TournamentEventScheduleDay>) {
    setSchedule((current) => ({
      ...current,
      days: sortDays(
        current.days.map((day) =>
          day.id === dayId
            ? {
                ...day,
                ...patch,
                activities: patch.activities
                  ? sortActivities(patch.activities)
                  : day.activities,
              }
            : day,
        ),
      ),
    }));
  }

  function updateActivity(
    dayId: string,
    activityId: string,
    patch: Partial<TournamentEventScheduleActivity>,
  ) {
    setSchedule((current) => ({
      ...current,
      days: current.days.map((day) =>
        day.id === dayId
          ? {
              ...day,
              activities: sortActivities(
                day.activities.map((activity) =>
                  activity.id === activityId ? { ...activity, ...patch } : activity,
                ),
              ),
            }
          : day,
      ),
    }));
  }

  function addActivity(dayId: string) {
    updateDay(dayId, {
      activities: [
        ...(selectedDay?.activities || []),
        {
          ...makeDefaultActivity(selectedDay?.activities.length || 0),
          startTime: selectedDay?.startTime || "08:00",
        },
      ],
    });
  }

  function duplicateActivity(dayId: string, activity: TournamentEventScheduleActivity) {
    updateDay(dayId, {
      activities: [
        ...(selectedDay?.activities || []),
        {
          ...activity,
          id: makeLocalId("activity"),
          title: activity.title ? `${activity.title} (copia)` : "",
        },
      ],
    });
  }

  function deleteActivity(dayId: string, activityId: string) {
    updateDay(dayId, {
      activities: (selectedDay?.activities || []).filter(
        (activity) => activity.id !== activityId,
      ),
    });
  }

  useEffect(() => {
    onPendingChangesChange?.(hasPendingChanges ? ["cronograma"] : []);
  }, [hasPendingChanges, onPendingChangesChange]);

  const saveSchedule = useCallback(async () => {
    if (saving) return false;
    if (!hasPendingChanges) return true;
    setSaving(true);
    try {
      const payload = {
        ...schedule,
        days: schedule.days.map((day) => ({
          ...day,
          activities: day.activities.map((activity) => ({
            ...activity,
            type: "general" as const,
            customTypeName: "",
            categoryKey: "",
            categoryLabel: "",
            locationLabel: "",
            isHighlighted: false,
            isActive: true,
          })),
        })),
      };
      const saved = await saveAdminTournamentEventSchedule(token, tournamentId, payload);
      const windows = buildConfiguredDayWindows({
        config: scheduleConfig,
        scheduleEndAt,
        scheduleStartAt,
      });
      const nextSchedule = windows.length
        ? mergeScheduleWithConfiguredDays(saved, windows)
        : {
            ...saved,
            days: sortDays(saved.days || []).map((day) => ({
              ...day,
              activities: sortActivities(day.activities || []),
            })),
          };
      setSchedule(nextSchedule);
      setBaselineSnapshot(scheduleSnapshot(nextSchedule));
      setSelectedDayId(
        (current) =>
          nextSchedule.days.find((day) => day.id === current)?.id ||
          nextSchedule.days[0]?.id ||
          "",
      );
      onPendingChangesChange?.([]);
      onSuccess("Cronograma salvo.");
      return true;
    } catch (error) {
      onError(formatScheduleSaveError(error));
      return false;
    } finally {
      setSaving(false);
    }
  }, [
    hasPendingChanges,
    onError,
    onPendingChangesChange,
    onSuccess,
    saving,
    schedule,
    scheduleConfig,
    scheduleEndAt,
    scheduleStartAt,
    token,
    tournamentId,
  ]);

  useImperativeHandle(
    ref,
    () => ({
      savePendingChanges: saveSchedule,
    }),
    [saveSchedule],
  );

  if (loading) {
    return <p className="admin-tournament-event-schedule-muted">Carregando cronograma...</p>;
  }

  return (
    <div className="admin-tournament-event-schedule">
      <div className="admin-tournament-event-schedule-toolbar">
        <label className="admin-tournament-event-schedule-switch">
          <input
            type="checkbox"
            checked={schedule.isPublicEnabled}
            onChange={(event) =>
              setSchedule((current) => ({
                ...current,
                isPublicEnabled: event.target.checked,
              }))
            }
          />
          <span>Exibir cronograma na pagina publica</span>
        </label>
        <div className="admin-tournament-event-schedule-actions">
          <Button variant="ghost" href={`#/torneios/${tournamentId}`}>
            Visualizar publico
          </Button>
        </div>
      </div>

      {schedule.days.length === 0 ? (
        <div className="admin-tournament-event-schedule-empty">
          <span className="material-symbols-outlined" aria-hidden="true">
            calendar_month
          </span>
          <p>
            Configure os dias e horarios do torneio em Local e horarios para
            montar as atividades do cronograma.
          </p>
        </div>
      ) : (
        <>
          <div className="admin-tournament-event-schedule-days">
            {schedule.days.map((day) => (
              <div
                key={day.id}
                className={`admin-tournament-event-schedule-day${
                  day.id === (selectedDay?.id || "") ? " is-active" : ""
                }`}
              >
                <button type="button" onClick={() => setSelectedDayId(day.id)}>
                  <span>{day.title || formatDateLabel(day.date)}</span>
                  <small>{day.activities.length} atividades</small>
                  <small>
                    {day.startTime || "--:--"} às {day.endTime || "--:--"}
                  </small>
                  <em>{day.isActive ? "Ativo" : "Inativo"}</em>
                </button>
              </div>
            ))}
          </div>

          {selectedDay ? (
            <div className="admin-tournament-event-schedule-editor">
              <div className="admin-tournament-event-schedule-activity-head">
                <h4>Atividades do dia</h4>
                <Button
                  type="button"
                  variant="ghost"
                  className="admin-tournament-event-schedule-add-activity"
                  onClick={() => addActivity(selectedDay.id)}
                  aria-label="Adicionar atividade"
                  title="Adicionar atividade"
                >
                  <span className="material-symbols-outlined" aria-hidden="true">
                    add
                  </span>
                </Button>
              </div>

              <div className="admin-tournament-event-schedule-activities">
                {selectedDay.activities.map((activity) => (
                  <article key={activity.id} className="admin-tournament-event-schedule-activity">
                    <div className="admin-tournament-event-schedule-activity-grid">
                      <OrganizerTimeInput
                        label="Inicio"
                        value={activity.startTime}
                        onChange={(value) =>
                          updateActivity(selectedDay.id, activity.id, { startTime: value })
                        }
                      />
                      <OrganizerTimeInput
                        label="Fim"
                        value={activity.endTime || ""}
                        onChange={(value) =>
                          updateActivity(selectedDay.id, activity.id, { endTime: value })
                        }
                      />
                      <label className="field">
                        <span>Titulo</span>
                        <input
                          type="text"
                          value={activity.title}
                          onChange={(event) =>
                            updateActivity(selectedDay.id, activity.id, {
                              title: event.target.value,
                            })
                          }
                          placeholder="Ex.: Jogos categoria 6F"
                        />
                      </label>
                      <label className="field">
                        <span>Observação</span>
                        <textarea
                          value={activity.description || ""}
                          onChange={(event) =>
                            updateActivity(selectedDay.id, activity.id, {
                              description: event.target.value,
                            })
                          }
                          maxLength={600}
                        />
                      </label>
                    </div>
                    <div className="admin-tournament-event-schedule-activity-actions">
                      <button
                        type="button"
                        onClick={() => duplicateActivity(selectedDay.id, activity)}
                        aria-label="Duplicar atividade"
                      >
                        <span className="material-symbols-outlined" aria-hidden="true">
                          content_copy
                        </span>
                      </button>
                      <button
                        type="button"
                        className="is-danger"
                        onClick={() => deleteActivity(selectedDay.id, activity.id)}
                        aria-label="Remover atividade"
                      >
                        <span className="material-symbols-outlined" aria-hidden="true">
                          delete
                        </span>
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          ) : null}
        </>
      )}

      {warnings.length > 0 ? (
        <div className="admin-tournament-event-schedule-warnings" role="status">
          <span className="material-symbols-outlined" aria-hidden="true">
            info
          </span>
          <ul>
            {warnings.slice(0, 6).map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
});

export default AdminTournamentEventSchedulePanel;
